import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Avatar } from '../../components/ui/Avatar';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton, SkeletonKPIRow, SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useKanbanDnd } from '../../lib/dnd';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { fmtDate } from '../../lib/utils';
import {
  tasksSummaryQuery, tasksListQuery, assignableUsersQuery, clientsLiteQuery,
  SCOPES, SCOPE_LABEL, dueInfo, contextLine, shortName, fullName, isDone, isHigh, incollaPiuRighe,
  type TaskItem, type TaskScope, type TaskDueFilter, type TaskListResponse, type TaskUserLite,
} from './tasksApi';
import { useTaskActions } from './useTaskActions';
import { OpzioniPersone } from './OpzioniPersone';
import { CalendarFeedPopover } from './CalendarFeed';
import { TasksMobile } from './TasksMobile';
import { TasksDesktop } from './TasksDesktop';
import { leggibile } from '../../components/mobile/MobileKit';
import './tasks.css';

/**
 * Pagina Task. Computer e telefono sono due pagine con la stessa forma (lista a
 * gruppi per scadenza): TasksDesktop (ridisegnata il 26/10/2026) e TasksMobile.
 * Qui restano i pezzi che usano tutte e due: l'aggiunta veloce, la lista del
 * telefono e la bacheca a due colonne. La pagina del computer di prima (numeri
 * in testa, linguette, tabella) è copiata in
 * os-planning/ridisegno/prima-del-ridisegno/lavoro.
 */
export function TasksPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><TasksMobile /></div> : <TasksDesktop />;
}

// ════════════════════════════════════════════════════════════════════
// Quick add: titolo + assegnatario (default: io) + scadenza · Invio crea
//
// d-only: sul telefono non c'è. Tre campi in colonna sono 140px di modulo
// sopra la lista, e l'azione "nuovo task" è già in fondo allo schermo, dove
// arriva il pollice (os-planning/mobile.md §4). Scrivere al volo è un gesto
// da tastiera vera: qui si viene a leggere e a chiudere task.
// ════════════════════════════════════════════════════════════════════
export function QuickAdd({ users, meId }: { users: TaskUserLite[]; meId: string }) {
  const { quickCreate } = useTaskActions();
  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState(meId);
  const [dueDate, setDueDate] = useState('');
  useEffect(() => { if (!assigneeId && meId) setAssigneeId(meId); }, [meId, assigneeId]);

  const submit = () => {
    const t = title.trim();
    if (!t || quickCreate.isPending) return;
    quickCreate.mutate({ title: t, assigneeId: assigneeId || null, dueDate: dueDate || null });
    setTitle(''); // assegnatario e data restano: salva-e-aggiungi-un-altro
  };

  return (
    <form className="tk-quick d-only" onSubmit={(e) => { e.preventDefault(); submit(); }} aria-label="Aggiungi task al volo">
      <div className="tk-quick-title">
        <Icon name="plus" size={12} className="mute" />
        <input
          className="inp"
          placeholder="Nuovo task: scrivi il titolo e premi Invio"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setTitle(''); }}
          // Più righe incollate: la finestra con l'anteprima, con persona e scadenza già scelte qui.
          onPaste={(e) => {
            const aperta = incollaPiuRighe(e, title, (testo) =>
              useModalStore.getState().open('newTask', { prefill: { testo, assigneeId, dueDate } }));
            if (aperta) setTitle('');
          }}
          aria-label="Titolo del task"
        />
      </div>
      <select className="inp" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} aria-label="Assegna a">
        <OpzioniPersone users={users} meId={meId} />
      </select>
      <input className="inp mono" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} aria-label="Scadenza" />
      {/* Sempre attivo (Lorenzo, 28/10: "perché qui non mi fa aggiungere le task?"): spento
          finché il titolo era vuoto sembrava rotto. Col titolo crea la task al volo; senza,
          apre la finestra completa con chi la fa e la scadenza già scelti qui. */}
      <button
        className="btn btn-primary"
        type="button"
        disabled={quickCreate.isPending}
        title={title.trim() ? 'Crea la task' : 'Apre la finestra per scrivere la task'}
        onClick={() => (title.trim()
          ? submit()
          : useModalStore.getState().open('newTask', { prefill: { assigneeId, dueDate: dueDate || undefined } }))}
      >
        Aggiungi
      </button>
    </form>
  );
}

// ════════════════════════════════════════════════════════════════════
// TELEFONO — la lista dei task come la fanno le app dei promemoria
//
// Il 6/10/2026, a 390px, ogni task era una scheda alta 220px: titolo,
// "ASSEGNATO A" e "SCADENZA" in due colonne con le etichette sopra, un filo,
// e il bottone "Completa" su una riga sua. Ne stavano due e mezzo per
// schermata. Qui un task è una riga: il cerchio a sinistra lo completa con un
// tocco (lo stesso gesto, con la stessa notifica e lo stesso "Annulla"), il
// resto della riga apre il dettaglio. Sotto il titolo, in una riga di testo,
// le stesse informazioni della tabella: scadenza, priorità, chi lo fa, per
// chi. Ne stanno otto per schermata.
// Sul computer questo componente non nasce: lì resta la tabella.
// ════════════════════════════════════════════════════════════════════
export function MobileTaskList({ items, canWrite, busy, onOpen, onToggle, senzaScadenza }: {
  items: TaskItem[];
  canWrite: boolean;
  busy: boolean;
  onOpen: (id: string) => void;
  onToggle: (t: TaskItem) => void;
  /** Il gruppo dice già la scadenza ("Oggi", "Senza scadenza"): la riga non la ripete. */
  senzaScadenza?: boolean;
}) {
  return (
    <ul className="m-tasks" aria-label="Task">
      {items.map((t) => {
        const done = isDone(t.status);
        // Cliente (reso leggibile: "NEW OMNIA S.R.L." → "New Omnia S.r.l.") e codice del progetto, come contextLine.
        const ctx = [t.client?.shortName ? leggibile(t.client.shortName) : null, t.project?.code].filter(Boolean).join(' · ');
        const due = done ? null : scadenzaInParole(t.dueDate);
        return (
          <li key={t.id} className={`m-task${done ? ' is-done' : ''}`}>
            {canWrite ? (
              <button
                type="button"
                className="m-task-check"
                aria-pressed={done}
                aria-label={done ? `Riapri il task: ${t.title}` : `Segna come completato: ${t.title}`}
                disabled={busy}
                onClick={() => onToggle(t)}
              >
                <span className="m-task-ring">{done && <Icon name="check" size={13} strokeWidth={2.5} />}</span>
              </button>
            ) : (
              <span className="m-task-check" aria-hidden="true"><span className="m-task-ring">{done && <Icon name="check" size={13} strokeWidth={2.5} />}</span></span>
            )}
            <button type="button" className="m-task-body" onClick={() => onOpen(t.id)}>
              <span className="m-task-title">
                {t.title}
                {t.recurrence && t.recurrence !== 'NONE' && <Icon name="repeat" size={12} className="m-task-rep" />}
              </span>
              <span className="m-task-meta">
                {done
                  ? <span>completato il {fmtDate(t.completedAt)}</span>
                  : !senzaScadenza && <span className={due!.tone === 'alert' ? 'text-alert font-semibold' : due!.tone === 'warn' ? 'text-warn' : undefined}>{due!.testo}</span>}
                {isHigh(t.priority) && !done && <span className="text-alert">priorità alta</span>}
                <span>{t.assignee ? shortName(t.assignee) : 'non assegnato'}</span>
                {ctx && <span>{ctx}</span>}
              </span>
            </button>
            <Icon name="chevronR" size={14} className="m-task-go" />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * La scadenza in una frase che si legge da sola, senza la colonna "Scadenza"
 * accanto: "scade oggi", "scade tra 3 g", "in ritardo di 4 g", "scade il
 * 12/10/2026". Le parole e i toni sono quelli di dueInfo, come nella tabella.
 */
function scadenzaInParole(iso: string | null | undefined): { testo: string; tone: 'alert' | 'warn' | 'mute' } {
  const d = dueInfo(iso);
  if (d.days == null) return { testo: 'senza scadenza', tone: 'mute' };
  if (d.days < 0) return { testo: d.label, tone: d.tone };
  if (d.days <= 6) return { testo: `scade ${d.label}`, tone: d.tone };
  return { testo: `scade il ${d.label}`, tone: d.tone };
}

/** Priorità alta = puntino rosso (con o senza la parola "Alta"). */
function HighDot({ label }: { label?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-alert shrink-0" style={{ textDecoration: 'none' }} title="Priorità alta">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--alert)' }} />
      {label && 'Alta'}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// Kanban a due colonne: Da fare / Completate — DnD nativo (lib/dnd.ts),
// move ottimistico (in Completate = Completa con toast/undo, in Da fare = Riapri)
// ════════════════════════════════════════════════════════════════════
type KanbanCol = 'open' | 'done';
const KANBAN_COLS: Array<{ key: KanbanCol; label: string; dot: string }> = [
  { key: 'open', label: 'Da fare', dot: 'var(--mute-2)' },
  { key: 'done', label: 'Completate', dot: 'var(--ok)' },
];

function DropZone({ dnd, col, position, visible }: {
  dnd: ReturnType<typeof useKanbanDnd<KanbanCol>>;
  col: KanbanCol;
  position: number;
  visible: boolean;
}) {
  const active = dnd.isOverZone(col, position);
  if (!visible) return null;
  return <div {...dnd.dropZoneProps(col, position)} className={`kanban-dropzone${active ? ' is-over' : ''}`} />;
}

export function KanbanView({ queryKey, isLoading, items, canWrite, meId, hasFilters, onClearFilters, onOpen }: {
  queryKey: readonly unknown[];
  isLoading: boolean;
  items: TaskItem[];
  canWrite: boolean;
  meId: string;
  hasFilters: boolean;
  onClearFilters: () => void;
  onOpen: (id: string) => void;
}) {
  const { kanbanMove, quickCreate } = useTaskActions();

  // Da fare per ordine kanban (il drag riordina davvero); Completate per data di chiusura
  const columns = useMemo(() => ({
    open: items.filter((t) => !isDone(t.status)).sort((a, b) => a.order - b.order || (a.dueDate ?? '').localeCompare(b.dueDate ?? '')),
    done: items.filter((t) => isDone(t.status)).sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '')),
  }), [items]);

  const dnd = useKanbanDnd<KanbanCol>((taskId, col, position) => {
    if (!canWrite) return;
    const task = items.find((t) => t.id === taskId);
    if (!task) return;
    const wasDone = isDone(task.status);
    const toDone = col === 'done';
    const kind = toDone && !wasDone ? 'complete' : !toDone && wasDone ? 'reopen' : 'reorder';
    if (kind === 'reorder' && toDone) return; // in Completate l'ordine è la data di chiusura: niente da fare
    // Stato di arrivo: DONE, oppure quello aperto che aveva (TODO se torna dai completati)
    const status = toDone ? 'DONE' : (wasDone ? 'TODO' : task.status);
    // Nuovo order per fractional indexing tra i vicini della colonna (escluso il task che si muove)
    const siblings = columns[col].filter((t) => t.id !== taskId);
    const p = Math.max(0, Math.min(position, siblings.length));
    let order: number;
    if (siblings.length === 0) order = 1000;
    else if (p <= 0) order = siblings[0]!.order - 1000;
    else if (p >= siblings.length) order = siblings[siblings.length - 1]!.order + 1000;
    else order = (siblings[p - 1]!.order + siblings[p]!.order) / 2;
    kanbanMove.mutate({
      id: taskId, status, position: p, order, kind, queryKey,
      title: task.title, prevStatus: task.status, dueDate: task.dueDate, recurrence: task.recurrence,
    });
  });

  // Quick-add in cima a Da fare: butta giù task al volo, resta aperto (salva-e-aggiungi-un-altro)
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickText, setQuickText] = useState('');
  const submitQuick = () => {
    const title = quickText.trim();
    if (!title) { setQuickOpen(false); return; }
    quickCreate.mutate({ title, assigneeId: meId || null });
    setQuickText('');
  };

  if (isLoading) {
    return (
      <div className="kanban tk-kanban">
        {KANBAN_COLS.map((c) => (
          <div className="kanban-col" key={c.key}>
            <div className="kanban-col-hd"><Skeleton size="sm" w={90} /><Skeleton size="sm" w={24} /></div>
            <div className="kanban-col-body"><Skeleton size="block" h={64} /><Skeleton size="block" h={64} /></div>
          </div>
        ))}
      </div>
    );
  }

  if (!items.length && hasFilters) {
    return (
      <EmptyState
        brand
        title="Nessun task con questi filtri"
        description="Azzera i filtri per vedere gli altri."
        action={<button className="btn" onClick={onClearFilters}>Azzera i filtri</button>}
      />
    );
  }

  const anyDragging = dnd.isAnyDragging();

  return (
    <div className="kanban tk-kanban">
      {KANBAN_COLS.map((c) => {
        const cards = columns[c.key];
        const isOver = dnd.isOverColumn(c.key);
        return (
          <div className={`kanban-col${isOver ? ' is-over' : ''}`} key={c.key} {...(canWrite ? dnd.columnProps(c.key) : {})}>
            <div className="kanban-col-hd">
              <div className="kanban-col-title">
                <span className="dot" style={{ background: c.dot }} />
                {c.label}
              </div>
              <span className="kanban-col-count">{cards.length}</span>
            </div>
            <div className="kanban-col-body">
              {c.key === 'open' && canWrite && (quickOpen ? (
                <input
                  className="inp text-[13px] py-1.5"
                  autoFocus
                  placeholder="Titolo, Invio per aggiungere…"
                  value={quickText}
                  onChange={(e) => setQuickText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); submitQuick(); }
                    else if (e.key === 'Escape') { setQuickOpen(false); setQuickText(''); }
                  }}
                  onBlur={() => { if (!quickText.trim()) setQuickOpen(false); }}
                  // Più righe incollate: la finestra con l'anteprima (qui la persona assegnata sono io).
                  onPaste={(e) => {
                    const aperta = incollaPiuRighe(e, quickText, (testo) =>
                      useModalStore.getState().open('newTask', { prefill: { testo, assigneeId: meId } }));
                    if (aperta) { setQuickText(''); setQuickOpen(false); }
                  }}
                  aria-label="Nuovo task"
                />
              ) : (
                <button
                  className="text-center py-2 border border-dashed border-line-2 rounded text-mute-2 text-[12px] hover:border-mute hover:text-mute w-full"
                  onClick={() => { setQuickOpen(true); setQuickText(''); }}
                >
                  <Icon name="plus" size={10} /> aggiungi
                </button>
              ))}
              <DropZone dnd={dnd} col={c.key} position={0} visible={anyDragging} />
              {cards.map((t, idx) => {
                const done = c.key === 'done';
                const d = dueInfo(t.dueDate, done);
                return (
                  <Fragment key={t.id}>
                    <div
                      className="kanban-card cursor-grab active:cursor-grabbing"
                      {...(canWrite ? dnd.cardProps(t.id, c.key) : {})}
                      onClick={() => onOpen(t.id)}
                    >
                      <div className={`kanban-card-title flex items-start gap-2${done ? ' line-through text-mute' : ''}`}>
                        <span className="min-w-0 flex-1">{t.title}</span>
                        {isHigh(t.priority) && !done && <span className="mt-1.5"><HighDot /></span>}
                      </div>
                      <div className="row between">
                        {t.assignee ? (
                          <div className="row text-[12px]" style={{ gap: 4 }}>
                            <Avatar initials={t.assignee.initials} size={18} name={fullName(t.assignee)} />
                            <span className="mute">{shortName(t.assignee)}</span>
                          </div>
                        ) : <span />}
                        <span className={`mono text-[12px] inline-flex items-center gap-1 ${d.tone === 'alert' ? 'text-alert font-semibold' : d.tone === 'warn' ? 'text-warn' : 'mute'}`}>
                          {t.recurrence && t.recurrence !== 'NONE' && <Icon name="repeat" size={10} className="mute-2" />}
                          {done ? fmtDate(t.completedAt) : d.label}
                        </span>
                      </div>
                    </div>
                    {/* In Completate la posizione non conta: una sola zona di drop (la colonna) */}
                    {c.key === 'open' && <DropZone dnd={dnd} col={c.key} position={idx + 1} visible={anyDragging} />}
                  </Fragment>
                );
              })}
              {!cards.length && !anyDragging && (
                <div className="text-center py-6 mute-2 text-[12px]">
                  {c.key === 'open' ? 'Niente da fare.' : 'Trascina qui un task per completarlo.'}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
